/**
 * Settings page — Halloween toggle, Discord channel/voice, backups, git
 * sync controls, owners list.
 */
import { useState, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Ghost, MessageSquare, GitBranch, Archive, Users, Volume2 } from "lucide-react";
import { usePlayer } from "../state/player";
import {
  useBackups,
  useCreateBackup,
  useDiscordStatus,
  useForceGitPush,
  useForceGitRestore,
  useGitSyncStatus,
  useHalloween,
  useRestoreBackup,
  useSetDiscordChannel,
  useSetHalloween,
  useTriggerGitSync,
  useVoiceChannels,
  useVoiceJoin,
  useVoiceLeave,
  useVoiceStatus,
} from "../hooks/useStats";
import { useOwners } from "../hooks/useTracks";
import { ApiRequestError, gitSyncApi, ownersApi } from "../lib/api";
import type { GitSyncEvent } from "@memes/shared";
import { ActivityPanel } from "../components/ActivityPanel";
import { formatGitSyncEvent, gitSyncPhaseLabel } from "../lib/gitSyncMessages";

export const Settings: React.FC = () => {
  const qc = useQueryClient();
  const halloween = useHalloween();
  const setHalloween = useSetHalloween();
  const discord = useDiscordStatus();
  const voiceStatus = useVoiceStatus();
  const voiceChannels = useVoiceChannels();
  const voiceJoin = useVoiceJoin();
  const voiceLeave = useVoiceLeave();
  const setChannel = useSetDiscordChannel();
  const backups = useBackups();
  const createBackup = useCreateBackup();
  const restoreBackup = useRestoreBackup();
  const gitStatus = useGitSyncStatus();
  const triggerSync = useTriggerGitSync();
  const forceRestore = useForceGitRestore();
  const forcePush = useForceGitPush();
  const ownersQuery = useOwners();

  const [channelInput, setChannelInput] = useState("");
  const [ownersInput, setOwnersInput] = useState("");
  const [voiceLeaveError, setVoiceLeaveError] = useState<string | null>(null);
  const normalize = usePlayer((s) => s.normalize);
  const normalizeGainDb = usePlayer((s) => s.normalizeGainDb);
  const setNormalize = usePlayer((s) => s.setNormalize);
  const setNormalizeGainDb = usePlayer((s) => s.setNormalizeGainDb);

  const [gitPhase, setGitPhase] = useState<string | null>(null);
  const [gitLines, setGitLines] = useState<string[]>([]);
  const [gitError, setGitError] = useState<string | null>(null);
  const [gitProgress, setGitProgress] = useState<{ current: number; total: number } | null>(
    null,
  );
  const gitEsRef = useRef<EventSource | null>(null);
  const gitAddedRef = useRef(0);
  const gitRemovedRef = useRef(0);

  const handleGitEvent = (ev: GitSyncEvent): void => {
    setGitPhase(gitSyncPhaseLabel(ev));
    setGitLines((prev) => [...prev, formatGitSyncEvent(ev)]);
    if (ev.type === "added" || ev.type === "updated") {
      gitAddedRef.current += 1;
      setGitProgress({
        current: gitAddedRef.current + gitRemovedRef.current,
        total: Math.max(gitAddedRef.current + gitRemovedRef.current, 1),
      });
    } else if (ev.type === "removed") {
      gitRemovedRef.current += 1;
      setGitProgress({
        current: gitAddedRef.current + gitRemovedRef.current,
        total: Math.max(gitAddedRef.current + gitRemovedRef.current, 1),
      });
    } else if (ev.type === "complete") {
      setGitProgress(null);
      void qc.invalidateQueries({ queryKey: ["tracks"] });
      void qc.invalidateQueries({ queryKey: ["git-sync"] });
    } else if (ev.type === "error") {
      setGitError(ev.message);
      setGitProgress(null);
    }
  };

  const startGitSyncStream = (): void => {
    gitEsRef.current?.close();
    gitAddedRef.current = 0;
    gitRemovedRef.current = 0;
    setGitLines([]);
    setGitPhase("Connecting…");
    setGitError(null);
    setGitProgress(null);

    const es = new EventSource(gitSyncApi.eventsUrl(), { withCredentials: true });
    gitEsRef.current = es;

    const types = [
      "start",
      "clone",
      "pull",
      "push",
      "scan",
      "added",
      "updated",
      "removed",
      "committed",
      "noop",
      "complete",
      "error",
    ] as const;

    for (const type of types) {
      es.addEventListener(type, (raw) => {
        try {
          const data = JSON.parse((raw as MessageEvent).data) as GitSyncEvent;
          handleGitEvent(data);
          if (data.type === "complete" || data.type === "error") {
            es.close();
            gitEsRef.current = null;
          }
        } catch {
          /* ignore malformed */
        }
      });
    }
  };

  useEffect(() => {
    return () => {
      gitEsRef.current?.close();
      gitEsRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (ownersQuery.data) setOwnersInput(ownersQuery.data.join(", "));
  }, [ownersQuery.data]);

  useEffect(() => {
    if (!gitStatus.data?.syncing || gitEsRef.current) return;
    startGitSyncStream();
  }, [gitStatus.data?.syncing]);

  const saveOwners = async (): Promise<void> => {
    const list = ownersInput
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    await ownersApi.set(list);
    ownersQuery.refetch();
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Settings</h1>

      <section className="card p-4">
        <div className="mb-2 flex items-center gap-2">
          <Ghost className="h-4 w-4 text-orange-500" />
          <h2 className="text-sm font-semibold">Halloween mode</h2>
        </div>
        <p className="mb-3 text-xs text-gray-500">
          1-in-10 chance a song is replaced with a spooky track from the misc folder.
        </p>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={halloween.data?.enabled ?? false}
            onChange={(e) => setHalloween.mutate(e.target.checked)}
          />
          Enabled
        </label>
      </section>

      <section className="card p-4">
        <div className="mb-2 flex items-center gap-2">
          <Volume2 className="h-4 w-4 text-indigo-500" />
          <h2 className="text-sm font-semibold">Loudness normalization</h2>
        </div>
        <p className="mb-3 text-xs text-gray-500">
          Levels quiet and loud tracks toward a similar perceived volume in the browser and in
          Discord voice (requires per-track loudness scan on upload or via Dev → scan loudness).
        </p>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={normalize}
            onChange={(e) => setNormalize(e.target.checked)}
          />
          Normalize loudness
        </label>
        <label className="mt-3 block text-xs text-gray-500">
          Fine-tune (dB): {normalizeGainDb}
          <input
            type="range"
            min={-12}
            max={6}
            step={0.5}
            value={normalizeGainDb}
            onChange={(e) => setNormalizeGainDb(Number(e.target.value))}
            className="mt-1 block w-full accent-indigo-600"
            disabled={!normalize}
          />
        </label>
      </section>

      <section className="card p-4">
        <div className="mb-2 flex items-center gap-2">
          <Users className="h-4 w-4" />
          <h2 className="text-sm font-semibold">Owners</h2>
        </div>
        <p className="mb-3 text-xs text-gray-500">
          Comma-separated list of owner names shown in upload + shuffle UIs.
        </p>
        <div className="flex gap-2">
          <input
            value={ownersInput}
            onChange={(e) => setOwnersInput(e.target.value)}
            className="flex-1 rounded-xl border px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-700"
            placeholder="Josh, Eden, Nicolas, David"
          />
          <button className="btn btn-primary" onClick={saveOwners}>
            Save
          </button>
        </div>
      </section>

      <section className="card p-4">
        <div className="mb-2 flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-indigo-500" />
          <h2 className="text-sm font-semibold">Discord</h2>
        </div>
        <p className="mb-1 text-xs text-gray-500">
          Status:{" "}
          {discord.data?.loggedIn ? (
            <span className="text-green-600">
              ✓ connected as {discord.data.user?.tag}
            </span>
          ) : (
            <span className="text-gray-500">— offline</span>
          )}
        </p>
        <p className="mb-3 text-xs text-gray-500">
          Allowed channel: <code>{discord.data?.allowedChannelId ?? "—"}</code>
        </p>
        <div className="mb-3 flex gap-2">
          <input
            value={channelInput}
            onChange={(e) => setChannelInput(e.target.value)}
            placeholder="Channel ID"
            className="flex-1 rounded-xl border px-3 py-2 text-sm dark:bg-gray-900 dark:border-gray-700"
          />
          <button
            className="btn btn-primary"
            disabled={!channelInput}
            onClick={() => {
              void setChannel.mutateAsync(channelInput).then(() => setChannelInput(""));
            }}
          >
            Set
          </button>
        </div>

        <div className="mt-2 border-t pt-3 dark:border-gray-800">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
            Voice channel
          </h3>
          {voiceStatus.data?.connected ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm">
                  Connected to <code>{voiceStatus.data.channelId}</code>
                </span>
                <button
                  className="btn btn-danger"
                  disabled={voiceLeave.isPending}
                  onClick={() => {
                    setVoiceLeaveError(null);
                    void voiceLeave
                      .mutateAsync()
                      .catch((err: unknown) => {
                        const msg =
                          err instanceof ApiRequestError
                            ? err.message
                            : err instanceof Error
                              ? err.message
                              : "Failed to leave voice channel";
                        setVoiceLeaveError(msg);
                      });
                  }}
                >
                  {voiceLeave.isPending ? "Leaving…" : "Leave"}
                </button>
              </div>
              {voiceLeaveError ? (
                <p className="text-xs text-red-600 dark:text-red-400">{voiceLeaveError}</p>
              ) : null}
            </div>
          ) : (
            <div className="space-y-2">
              {voiceChannels.data?.channels.map((ch) => (
                <button
                  key={ch.id}
                  className="btn w-full justify-between"
                  onClick={() =>
                    voiceJoin.mutate({ channelId: ch.id, guildId: ch.guildId })
                  }
                >
                  <span>
                    {ch.guildName} · #{ch.name}
                  </span>
                  <span className="text-xs text-gray-500">Join</span>
                </button>
              )) ?? <p className="text-xs text-gray-500">No voice channels visible.</p>}
            </div>
          )}
        </div>
      </section>

      <section className="card p-4">
        <div className="mb-2 flex items-center gap-2">
          <GitBranch className="h-4 w-4 text-sky-500" />
          <h2 className="text-sm font-semibold">Git backup</h2>
        </div>
        <p className="mb-2 text-xs text-gray-500">
          The app library is the source of truth. Imports and uploads are backed up to the music
          repo automatically. Restore from the repo only when the remote backup is newer than your
          last library change.
        </p>
        <p className="mb-1 text-xs text-gray-500">
          Configured: {gitStatus.data?.configured ? "yes" : "no"}
          {gitStatus.data?.pendingBackup ? " · Pending backup" : ""}
          {" · "}Last sync:{" "}
          {gitStatus.data?.lastSync
            ? new Date(gitStatus.data.lastSync).toLocaleString()
            : "never"}
          {" · "}Last backup:{" "}
          {gitStatus.data?.lastBackup
            ? new Date(gitStatus.data.lastBackup).toLocaleString()
            : "never"}
          {" · "}Last restore:{" "}
          {gitStatus.data?.lastRestore
            ? new Date(gitStatus.data.lastRestore).toLocaleString()
            : "never"}
        </p>
        {gitStatus.data?.lastError && (
          <p className="mb-2 text-xs text-red-500">{gitStatus.data.lastError}</p>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            className="btn"
            disabled={!gitStatus.data?.configured || gitStatus.data.syncing}
            onClick={() => {
              startGitSyncStream();
              triggerSync.mutate();
            }}
          >
            {gitStatus.data?.syncing ? "Syncing…" : "Sync now"}
          </button>
          <button
            className="btn"
            disabled={!gitStatus.data?.configured || gitStatus.data.syncing}
            onClick={() => {
              startGitSyncStream();
              void forceRestore.mutate();
            }}
          >
            Restore from backup
          </button>
          <button
            className="btn"
            disabled={!gitStatus.data?.configured || gitStatus.data.syncing}
            onClick={() => {
              startGitSyncStream();
              void forcePush.mutate();
            }}
          >
            Force push
          </button>
        </div>
        <ActivityPanel
          title="Git backup"
          phase={gitPhase}
          lines={gitLines}
          error={gitError ?? gitStatus.data?.lastError}
          progress={gitProgress}
        />
      </section>

      <section className="card p-4">
        <div className="mb-2 flex items-center gap-2">
          <Archive className="h-4 w-4 text-amber-500" />
          <h2 className="text-sm font-semibold">Backups</h2>
        </div>
        <p className="mb-3 text-xs text-gray-500">
          Up to {backups.data?.maxBackups ?? 10} snapshots of the database are kept.
        </p>
        <button className="btn btn-primary mb-3" onClick={() => createBackup.mutate()}>
          Create backup
        </button>
        <ul className="space-y-1 text-sm">
          {backups.data?.backups.map((b) => (
            <li key={b.filename} className="flex items-center justify-between gap-3">
              <code className="truncate">{b.filename}</code>
              <button
                className="btn"
                onClick={() => {
                  if (confirm(`Restore from ${b.filename}?`))
                    void restoreBackup.mutate(b.filename);
                }}
              >
                Restore
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};
